import { useCallback, useMemo, useState } from "react";

import { Button } from "@/components/ui/button/button";

import { useHandlePrint } from "../../_hooks";
import * as Model from "../model";
import { GuestListTable } from "./guest-list-table";

import type * as Repository from "../../_repository";
import type * as CreateOptionPms from "../../create-option-pms";

type Props = {
	hotelID: string;
	// 検索日
	searchDate: string;
	// オプションリスト
	optionPmsActiveListQueryData: Repository.Query.QueryData.OptionPmsList.OptionPmsActiveListQueryData;

	// print component
	printGuestListForOptionPmsChildren: React.ReactNode;
	// オプションの作成ダイアログ
	createOptionPmsDialogChildren: (props: CreateOptionPms.Props) => JSX.Element;

	// オプションカラーリスト
	optionPmsColorListQueryData: Repository.Query.QueryData.OptionPmsColorListQueryData;
};

export const GuestListTableForOptionPms = ({
	hotelID,
	searchDate,
	optionPmsActiveListQueryData,
	//
	printGuestListForOptionPmsChildren,
	// オプションの作成ダイアログ
	createOptionPmsDialogChildren,
	// オプションカラーリスト
	optionPmsColorListQueryData,
}: Props) => {
	/**
	 * 初期化
	 */
	const {
		// readonly
		viewData,
		// changeable
		// オプションの選択
		onSetCardIDOfOption,
		// オプションの選択解除
		onClearCardIDOfOption,
		// mutable
		// オプションのキャンセル
		onCancelOptionPmsItem,
	} = Model.UseCase.useInitialized({
		hotelID,
		searchDate,
		optionPmsActiveListQueryData,
		optionPmsColorListQueryData,
	});

	const { onGenerateIdForPrintSectionEl, onPrint } = useHandlePrint();
	/**
	 * print-section id を生成
	 */
	const idForPrintSectionEl = useMemo(
		() => onGenerateIdForPrintSectionEl({ prefix: "option-pms-guest-list" }),
		[onGenerateIdForPrintSectionEl],
	);
	const handleClickPrint = useCallback(() => {
		onPrint();
	}, [onPrint]);

	const [canShowCreateOptionPms, setCanShowCreateOptionPms] = useState(false);
	/**
	 * オプション作成ダイアログの表示制御
	 */
	const handleClickOpenCreateOptionPms = useCallback(() => {
		setCanShowCreateOptionPms(true);
	}, []);
	/**
	 * オプション作成ダイアログの終了時のコールバック
	 */
	const onCloseCreateOptionPms = useCallback(() => {
		setCanShowCreateOptionPms(false);
	}, []);
	/**
	 * オプション作成ダイアログ へ props を注入
	 */
	const RenderCreateOptionPmsDialog = useMemo(
		() =>
			canShowCreateOptionPms &&
			createOptionPmsDialogChildren({ hotelID, searchDate, onCloseCallback: onCloseCreateOptionPms }),
		[canShowCreateOptionPms, createOptionPmsDialogChildren, hotelID, onCloseCreateOptionPms, searchDate],
	);

	return (
		<>
			<div className="space-y-3">
				<div className="space-y-1">
					<p className="text-sm">予約者リスト</p>

					<div className="flex justify-between">
						<div className="flex flex-wrap items-center gap-x-2">
							<p className="text-sm">
								<b>検索結果:</b> {viewData.listCountText}
							</p>
						</div>

						<div className="grid grid-cols-2 gap-x-2">
							<Button variant="outline" type="button" size="sm" onClick={handleClickPrint}>
								印刷
							</Button>

							<Button variant="outline" type="button" size="sm" onClick={handleClickOpenCreateOptionPms}>
								追加
							</Button>
						</div>
					</div>
				</div>

				{/* テーブル */}
				<GuestListTable
					viewData={viewData}
					onSelectRow={onSetCardIDOfOption}
					onUnSelectRow={onClearCardIDOfOption}
					onCancelOptionPmsItem={onCancelOptionPmsItem}
				/>
			</div>

			{/* 印刷 */}
			<div id={idForPrintSectionEl} className="hidden print:block">
				{printGuestListForOptionPmsChildren}
			</div>

			{/* オプション作成ダイアログ */}
			{RenderCreateOptionPmsDialog}
		</>
	);
};
